import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import moment from "moment";
import { useNotifications } from "../../hooks/useNotifications";
import { Notification, NotificationDisplayData } from "../../app/data/notifications/notificationTypes";
import { ReactComponent as BellIcon } from "../../images/bell.svg";
import { ReactComponent as DocumentIcon } from "../../images/document.svg";
import { ReactComponent as TruckIcon } from "../../images/truck.svg";
import { ReactComponent as ExclamationIcon } from "../../images/exclamation.svg";
import DropdownPopup from "../../ui-components/dropdown-popup/dropdownPopup";
import "./notificationDropdown.scss";

const NotificationDropdown: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [unblocked, setUnblocked] = useState<boolean>(true);
  const history = useHistory();
  const { notifications, unreadCount, markAsRead, markAllAsRead, error } = useNotifications();

  const openDropdown = () => {
    if (!unblocked) return;
    setUnblocked(false);
    setIsOpen(true);
  };

  const closeDropdown = () => {
    setIsOpen(false);
    setTimeout(() => setUnblocked(true), 150);
  };

  const getRelativeTime = (dateString: string): string => {
    // Convert GMT/UTC timestamp to local time using moment
    const date = moment.utc(dateString).local();
    const now = moment();
    const diffInSeconds = now.diff(date, 'seconds');

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 604800) {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else {
      return date.format("MMM D, YYYY");
    }
  };

  const getNotificationIcon = (eventType: string) => {
    switch (eventType) {
      case "NEW_CLAIM":
        return <DocumentIcon className="notification-icon" />;
      case "PICKUP":
      case "PICKUP_UPDATE":
      case "PICKUP_RESCHEDULE":
      case "PICKUP_CANCELLED":
      case "PICKUP_REQUESTED":
      case "PICKUP_MISSED":
        return <TruckIcon className="notification-icon" />;
      case "DELIVERY":
        return <TruckIcon className="notification-icon" />;
      case "EXCEPTION":
        return <ExclamationIcon className="notification-icon exclamation-icon" />;
      default:
        return <DocumentIcon className="notification-icon" />;
    }
  };

  const formatNotificationData = (notification: Notification): NotificationDisplayData => {
    let title = "";
    let description = "";
    let probillNumber = null;

    try {
      const normalized = JSON.parse(notification.normalized);
      switch (notification.eventType) {
        case "NEW_CLAIM":
          title = `Claim #${normalized.claimNumber || "Unknown"}`;
          description = `New claim has been reported for BOL ${normalized.bol || "Unknown"}`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "PICKUP":
          title = `Pickup #${normalized.bol || normalized.bolNumber || "Unknown"}`;
          description = "Your parcel has been picked up and on its way to be delivered";
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "PICKUP_UPDATE":
          title = `Pickup Update #${normalized.formattedPickupNumber || normalized.pickupNumber || "Unknown"}`;
          description = `Pickup has been updated for BOL ${normalized.bolNumber || "Unknown"}`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "PICKUP_RESCHEDULE":
          title = `Pickup Rescheduled #${normalized.formattedPickupNumber || normalized.pickupNumber || "Unknown"}`;
          description = `Pickup has been rescheduled. Review updated details.`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "PICKUP_CANCELLED":
          title = `Pickup Cancelled #${normalized.formattedPickupNumber || normalized.pickupNumber || "Unknown"}`;
          description = `Pickup has been cancelled. No further action required.`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "PICKUP_REQUESTED":
          title = `Pickup Requested #${normalized.formattedPickupNumber || normalized.pickupNumber || "Unknown"}`;
          description = `New pickup request created. Review details.`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "PICKUP_MISSED":
          title = `Pickup Missed #${normalized.formattedPickupNumber || normalized.pickupNumber || "Unknown"}`;
          description = `Pickup was not completed on scheduled date. Action required.`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "DELIVERY":
          title = `Shipment #${normalized.bol || "Unknown"}`;
          description = "Parcel has been delivered";
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        case "EXCEPTION":
          title = `Exception #${normalized.itemSerial || "Unknown"}`;
          description = `${normalized.exceptionTitle || "Exception"}: ${normalized.probillNumber || normalized.description || "Exception occurred"}`;
          probillNumber = normalized.probillNumber;
          break;
        case "STATUS_CHANGE": // probill status change
          title = `Shipment #${normalized.probillNumber || "Unknown"}`;
          description = `Shipment status has been changed to ${normalized.status || "Updated"}`;
          probillNumber = normalized.probill || normalized.probillNumber;
          break;
        default:
          title = notification.eventType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
          description = "Notification received";
      }
    } catch (error) {
      title = notification.eventType.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
      description = "Notification received";
    }

    return {
      title,
      description,
      icon: { icon: "", color: "#4A90E2" },
      date: getRelativeTime(notification.occurredAt),
      isRead: notification.read,
      probillNumber
    };
  };

  const handleNotificationClick = (notification: Notification, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Mark as read if not already read
    if (!notification.read) {
      markAsRead(notification.deliveryId);
    }
    
    const displayData = formatNotificationData(notification);
    const source = (notification.source || "").toUpperCase();

    switch (source) {
      case "CLAIMS_SYSTEM": {
        if (displayData.probillNumber) {
          history.push(`/shipments/${displayData.probillNumber}?scrollTo=tabs&source=notification&tab=claims`);
          closeDropdown();
        }
        break;
      }
      case "PROBILL": {
        if (displayData.probillNumber) {
          history.push(`/shipments/${displayData.probillNumber}?source=notification`);
          closeDropdown();
        }
        break;
      }
      case "EXCEPTION": {
        if (displayData.probillNumber) {
          history.push(`/shipments/${displayData.probillNumber}?scrollTo=tabs&source=notification&tab=exceptions`);
          closeDropdown();
        }
        break;
      }
      case "PICKUP": {
        // Route pickup notifications to pickup log using sourceId
        if (notification.sourceId) {
          history.push(`/pickup/log/${notification.sourceId}`);
          closeDropdown();
        } else if (displayData.probillNumber) {
          history.push(`/shipments/${displayData.probillNumber}?source=notification`);
          closeDropdown();
        }
        break;
      }
      default: {
        // Fallback: if we know probill, send to shipment details
        if (displayData.probillNumber) {
          history.push(`/shipments/${displayData.probillNumber}?source=notification`);
          closeDropdown();
        }
        break;
      }
    }
  };

  const handleMarkAllAsRead = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    markAllAsRead();
  };

  return (
    <div className="xgs-notification-dropdown">
      <div className="xgs-notification-dropdown__bell" onClick={openDropdown}>
        <BellIcon className="bell-icon" />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount}</span>
        )}
      </div>

      <DropdownPopup
        opened={isOpen}
        onClose={closeDropdown}
        className="xgs-notification-dropdown-popup"
        position="bottom right"
        contentStyle={{
          borderRadius: 8,
          border: "1px solid #E0E0E0",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
          padding: "0",
          zIndex: 9999,
          minWidth: "320px",
          maxWidth: "400px"
        }}
      >
        <div className="xgs-notification-dropdown-content">
          <div className="notification-header">
            <h3>Notifications</h3>
            <button 
              className="notification-mark-all"
              onClick={handleMarkAllAsRead}
              title="Mark all as read"
            >
              Mark all as read
            </button>
          </div>
          
          <div className="notification-list">
            {error || notifications.length === 0 ? (
              <div className="notification-empty">
                <p>No notifications</p>
              </div>
            ) : (
              notifications.map((notification, index) => {
                const displayData = formatNotificationData(notification);
                return (
                  <div
                    key={notification.deliveryId}
                    className={`notification-item ${!notification.read ? "unread" : ""}`}
                    onClick={(e) => handleNotificationClick(notification, e)}
                  >
                    <div className="notification-icon-wrapper">
                      {getNotificationIcon(notification.eventType)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-title">
                        {displayData.title}
                        {!notification.read && <span className="unread-dot"></span>}
                      </div>
                      <div className="notification-description">
                        {displayData.description}
                      </div>
                      <div className="notification-date">
                        {displayData.date}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </DropdownPopup>
    </div>
  );
};

export default NotificationDropdown;
